import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const RemindVendorsModal = ({ isOpen, onClose, vendors, onSend, isSending }) => {
  const [selected, setSelected] = useState({});

  // Initialize all vendors as checked when modal opens
  useEffect(() => {
    if (isOpen && vendors?.length > 0) {
      const initial = {};
      vendors?.forEach(v => {
        initial[v.email] = true;
      });
      setSelected(initial);
    }
  }, [isOpen, vendors]);

  if (!isOpen) return null;

  const toggleVendor = (email) => {
    setSelected(prev => ({ ...prev, [email]: !prev?.[email] }));
  };

  const toggleAll = () => {
    const allChecked = vendors?.every(v => selected?.[v?.email]);
    const next = {};
    vendors?.forEach(v => { next[v.email] = !allChecked; });
    setSelected(next);
  };

  const selectedVendors = vendors?.filter(v => selected?.[v?.email]);
  const allChecked = vendors?.every(v => selected?.[v?.email]);
  const someChecked = vendors?.some(v => selected?.[v?.email]);

  const handleSend = () => {
    if (selectedVendors?.length === 0) return;
    onSend(selectedVendors);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e?.target === e?.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{ background: 'white', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', border: '1.5px solid #fde8d8' }}
      >
        {/* Header */}
        <div
          className="px-6 py-5"
          style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B, #E0528A)' }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(255,255,255,0.2)', border: '1.5px solid rgba(255,255,255,0.3)' }}
              >
                <Icon name="Send" size={18} color="white" />
              </div>
              <div>
                <h2 className="text-white font-bold text-lg leading-tight">Send Reminder</h2>
                <p className="text-white text-xs" style={{ opacity: 0.85 }}>Select vendors to remind</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:opacity-70"
              style={{ background: 'rgba(255,255,255,0.2)' }}
            >
              <Icon name="X" size={16} color="white" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-sm text-gray-600 mb-4">
            Which vendors would you like to remind?
          </p>

          {vendors?.length === 0 ? (
            <div className="text-center py-8">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                style={{ background: '#fef3e8' }}
              >
                <Icon name="CheckCircle" size={22} style={{ color: '#F58A54' }} />
              </div>
              <p className="text-gray-500 text-sm font-medium">No vendors to remind</p>
              <p className="text-gray-400 text-xs mt-1">All invited vendors have already submitted quotes.</p>
            </div>
          ) : (
            <>
              {/* Select All */}
              <div
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl mb-2 cursor-pointer transition-colors hover:bg-orange-50"
                style={{ border: '1px solid #fde8d8' }}
                onClick={toggleAll}
              >
                <div
                  className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-all"
                  style={{
                    background: allChecked ? 'linear-gradient(135deg, #F58A54, #F06F6B)' : someChecked ? 'linear-gradient(135deg, #F58A54, #F06F6B)' : 'white',
                    border: allChecked || someChecked ? 'none' : '1.5px solid #d1d5db'
                  }}
                >
                  {allChecked && <Icon name="Check" size={12} color="white" />}
                  {!allChecked && someChecked && <span style={{ width: 8, height: 2, background: 'white', display: 'block', borderRadius: 1 }} />}
                </div>
                <span className="text-sm font-semibold text-gray-700">Select All</span>
                <span
                  className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full"
                  style={{ background: '#fef3e8', color: '#F58A54' }}
                >
                  {vendors?.length} vendor{vendors?.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Vendor List */}
              <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {vendors?.map((vendor) => {
                  const isChecked = !!selected?.[vendor?.email];
                  return (
                    <div
                      key={vendor?.email}
                      className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer transition-all"
                      style={{
                        border: isChecked ? '1.5px solid #fde8d8' : '1.5px solid #f3f4f6',
                        background: isChecked ? '#fffbf8' : 'white'
                      }}
                      onClick={() => toggleVendor(vendor?.email)}
                    >
                      {/* Checkbox */}
                      <div
                        className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-all"
                        style={{
                          background: isChecked ? 'linear-gradient(135deg, #F58A54, #F06F6B)' : 'white',
                          border: isChecked ? 'none' : '1.5px solid #d1d5db'
                        }}
                      >
                        {isChecked && <Icon name="Check" size={12} color="white" />}
                      </div>
                      {/* Vendor Avatar */}
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
                        style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
                      >
                        {vendor?.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      {/* Vendor Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{vendor?.name}</p>
                        <p className="text-xs text-gray-400 truncate">{vendor?.email}</p>
                      </div>
                      {/* Status badge */}
                      <span
                        className="text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{ background: '#fef3e8', color: '#F58A54' }}
                      >
                        Pending
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {vendors?.length > 0 && (
          <div
            className="px-6 py-4 flex items-center justify-between gap-3"
            style={{ borderTop: '1px solid #fde8d8', background: '#fffbf8' }}
          >
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-medium text-gray-600 border transition-colors hover:bg-gray-50"
              style={{ borderColor: '#e5e7eb' }}
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={selectedVendors?.length === 0 || isSending}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #F58A54, #F06F6B)' }}
            >
              {isSending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Icon name="Send" size={14} color="white" />
                  Send Reminder Email{selectedVendors?.length > 1 ? `s (${selectedVendors?.length})` : ''}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RemindVendorsModal;
